/**
 * Le secret partagé d'un boîtier GPS, posé depuis l'écran Camions.
 *
 * L'ingestion `POST /tracking/hardware/gps` est fail-closed sur ce secret: un
 * camion qui n'en a pas voit ses positions refusées, et c'est voulu — sans lui,
 * quiconque devine un `device_id` peut falsifier la trace d'un trajet, donc la
 * preuve de livraison et les alertes de zone.
 *
 * Mais l'écran supprimait `gps_config_json` du payload à la création comme à la
 * modification. Aucun boîtier ne pouvait donc être mis en service autrement que
 * par un appel HTTP manuel: la documentation demandait un secret que rien dans
 * le produit ne permettait de saisir.
 *
 * Deux règles portées ici plutôt qu'en ligne dans le formulaire, parce qu'elles
 * décident d'un secret et méritent d'être testées:
 *
 *  - Un champ laissé vide en modification ne vaut pas « efface le secret ». Un
 *    exploitant qui vient corriger une plaque couperait sinon la remontée GPS
 *    de son camion sans jamais l'avoir demandé.
 *  - Le secret existant n'est jamais renvoyé à l'écran. On dit qu'il est posé,
 *    on ne le réaffiche pas: un identifiant qui traîne dans un DOM finit dans
 *    une capture d'écran.
 */

/** Le camion porte-t-il déjà un secret ? Sans révéler lequel. */
export const hasGpsSecret = (truck) => {
  const config = truck?.gps_config_json;
  if (!config || typeof config !== 'object') return false;
  return Boolean(config.shared_secret || config.api_key);
};

/**
 * Ce qu'il faut envoyer pour `gps_config_json`, ou `undefined` pour ne pas
 * toucher à l'existant.
 */
export const buildGpsConfigPayload = (secretInput, existingTruck = null) => {
  const secret = String(secretInput || '').trim();
  if (secret) {
    // La configuration peut porter d'autres clés propres au fournisseur: on
    // remplace le secret sans jeter le reste.
    const existing = existingTruck?.gps_config_json;
    const base = existing && typeof existing === 'object' ? existing : {};
    return { ...base, shared_secret: secret };
  }
  return undefined;
};

/** Ce que l'écran doit dire de l'état du secret, en toutes lettres. */
export const gpsSecretHint = (truck) =>
  hasGpsSecret(truck)
    ? 'Un secret est déjà enregistré. Laissez vide pour le conserver.'
    : "Sans secret, les positions de ce boîtier seront refusées.";
